import {
	Checkbox,
	Col,
	Collapse,
	Divider,
	Form,
	Input,
	Radio,
	Row,
	Select,
	Space,
	Tag,
	Typography,
} from 'antd';
import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import type { FieldConfig } from './QCAccordionForm';
import { buildRows, renderRows } from './QCAccordionForm';
import { useQCPraPencairanData } from '@/pages/robotic/qc-pra-pencairan/QCPraPencairanContext';
import { applyFieldMeta } from '@/pages/robotic/qc-pra-pencairan/qc-pd-mapping';
import { useSelectAddress } from '@/libs/fleet-api/hooks/master-data';
import { inputRtRw } from '@/libs/antd/input-number';

// ─── Types ───────────────────────────────────────────────────────────────────

type AssetType = 'new' | 'split-po';
type BpkbOwnerType = 'customer' | 'others';
type OthersCustomerType = 'personal' | 'company';

type AddressMasterData = {
	postalId: number;
	postalCode: string;
	locationName: string;
	districtName: string;
	cityName: string;
};

// ─── Options ─────────────────────────────────────────────────────────────────


const CONDITION_LABELS: Record<number, string> = {
	1: 'NEW + Pemilik BPKB Baru Atas Nama Customer',
	2: 'NEW + Registrasi Aset Others - Personal',
	3: 'NEW + Registrasi Aset Others - Company',
	4: 'SPLIT PO/HE/USED + Pemilik BPKB Baru Atas Nama Customer',
	5: 'SPLIT PO/HE/USED + Registrasi Aset Others - Personal',
	6: 'SPLIT PO/HE/USED + Registrasi Aset Others - Company',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Render a group of fields into rows, applying API field metadata for disabled/required/options. */
const renderGroup = (
	fields: FieldConfig[],
	readOnly: boolean,
	fieldMeta: ReturnType<typeof useQCPraPencairanData>['fieldMeta'],
) => renderRows(buildRows(applyFieldMeta(fields, fieldMeta)), readOnly);

const fillAddress = (
	form: ReturnType<typeof Form.useFormInstance>,
	prefix: string,
	addr: { alamat: string; rt: string; rw: string; kodePos: string; kelurahan: string; kecamatan: string; kota: string },
) => {
	form.setFieldsValue({
		[`${prefix}Alamat`]: addr.alamat,
		[`${prefix}Rt`]: addr.rt,
		[`${prefix}Rw`]: addr.rw,
		[`${prefix}KodePos`]: addr.kodePos,
		[`${prefix}Kelurahan`]: addr.kelurahan,
		[`${prefix}Kecamatan`]: addr.kecamatan,
		[`${prefix}Kota`]: addr.kota,
		// Mirror into the composite address field so the postal Select can display the saved
		// postalCode label without needing a re-fetch (postalId stays 0 until user re-picks).
		[`${prefix}Address`]: {
			postalId: 0,
			postalCode: addr.kodePos,
			locationName: addr.kelurahan,
			districtName: addr.kecamatan,
			cityName: addr.kota,
		},
	});
};

// ─── Address Section (postal-picker pattern) ────────────────────────────────
//
// One reusable address block for BPKB / BPKB Baru. Postal selection drives Kelurahan /
// Kecamatan / Kota (derived, display-only) and mirrors postalCode + parts back into the
// flat `${prefix}KodePos`/`Kelurahan`/`Kecamatan`/`Kota` fields used by save mapping.

const QCAssetAddressSection: FC<{
	prefix: string;
	readOnly: boolean;
	forceDisabled?: boolean;
}> = ({ prefix, readOnly, forceDisabled = false }) => {
	const form = Form.useFormInstance();
	const selectAddress = useSelectAddress();
	const { fieldMeta } = useQCPraPencairanData();

	const addressKey = `${prefix}Address`;
	const address = Form.useWatch(addressKey, form) as AddressMasterData | undefined;

	// Build composite from flat fields on first render when API returned strings without the
	// composite. postalId=0 is a placeholder until the user re-picks from the lookup.
	const hydratedRef = useRef(false);
	useEffect(() => {
		if (hydratedRef.current) return;
		const existing = form.getFieldValue(addressKey);
		if (existing) {
			hydratedRef.current = true;
			return;
		}
		const kodePos = form.getFieldValue(`${prefix}KodePos`) as string | undefined;
		const kelurahan = form.getFieldValue(`${prefix}Kelurahan`) as string | undefined;
		const kecamatan = form.getFieldValue(`${prefix}Kecamatan`) as string | undefined;
		const kota = form.getFieldValue(`${prefix}Kota`) as string | undefined;
		if (kodePos || kelurahan || kecamatan || kota) {
			form.setFieldValue(addressKey, {
				postalId: 0,
				postalCode: kodePos ?? '',
				locationName: kelurahan ?? '',
				districtName: kecamatan ?? '',
				cityName: kota ?? '',
			});
			hydratedRef.current = true;
		}
	}, [addressKey, prefix, form]);

	// Sync composite → flat fields whenever the user picks a different postal.
	useEffect(() => {
		if (!address) return;
		form.setFieldsValue({
			[`${prefix}KodePos`]: address.postalCode ?? '',
			[`${prefix}Kelurahan`]: address.locationName ?? '',
			[`${prefix}Kecamatan`]: address.districtName ?? '',
			[`${prefix}Kota`]: address.cityName ?? '',
		});
	}, [address, prefix, form]);

	const isDisabled = (name: string, fallback = false) => {
		if (readOnly || forceDisabled) return true;
		const m = fieldMeta[name];
		if (m) return !m.editable;
		return fallback;
	};
	const isRequired = (name: string, fallback = true) => {
		const m = fieldMeta[name];
		return m ? m.required : fallback;
	};

	const lc = { span: 8 } as const;
	const wc = { span: 16 } as const;

	const displayKelurahan = address?.locationName;
	const displayKecamatan = address?.districtName;
	const displayKota = address?.cityName;

	return (
		<>
			<Row gutter={[40, 20]}>
				<Col span={24}>
					<Form.Item
						name={`${prefix}Alamat`}
						label='Alamat Lengkap'
						required={isRequired(`${prefix}Alamat`)}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: 20 }}
					>
						<Input.TextArea rows={4} disabled={isDisabled(`${prefix}Alamat`, true)} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label='RT/RW' required={isRequired(`${prefix}Rt`)} labelCol={lc} wrapperCol={wc}>
						<Row wrap={false} align='middle' gutter={8}>
							<Col flex='auto'>
								<Form.Item name={`${prefix}Rt`} noStyle>
									<Input {...inputRtRw} disabled={isDisabled(`${prefix}Rt`, true)} />
								</Form.Item>
							</Col>
							<Col flex='none'>/</Col>
							<Col flex='auto'>
								<Form.Item name={`${prefix}Rw`} noStyle>
									<Input {...inputRtRw} disabled={isDisabled(`${prefix}Rw`, true)} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={addressKey}
						label='Kode Pos Sesuai Identitas'
						required={isRequired(`${prefix}KodePos`)}
						labelCol={lc}
						wrapperCol={wc}
						getValueFromEvent={(_, option) => (option as { data?: AddressMasterData } | undefined)?.data}
						getValueProps={(value: AddressMasterData | undefined) => {
							if (!value || !value.postalCode) return {};
							return { value: { value: value.postalId, label: value.postalCode } };
						}}
					>
						<Select
							{...selectAddress}
							disabled={isDisabled(`${prefix}KodePos`)}
							placeholder='Ketik 3 digit kode pos...'
						/>
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label='Kelurahan Sesuai Identitas' required={isRequired(`${prefix}Kelurahan`)} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKelurahan} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label='Kecamatan Sesuai Identitas' required={isRequired(`${prefix}Kecamatan`)} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKecamatan} disabled />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={24}>
					<Form.Item label='Kota Sesuai Identitas' required={isRequired(`${prefix}Kota`)} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
						<Input value={displayKota} disabled />
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

// ─── Component ───────────────────────────────────────────────────────────────

const QCAssetDataForm: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const form = Form.useFormInstance();
	const { asset: assetData, fieldMeta } = useQCPraPencairanData();

	// Condition state — driven by API meta when available, fallback to manual toggle
	const [assetType, setAssetType] = useState<AssetType>('new');
	const [bpkbOwner, setBpkbOwner] = useState<BpkbOwnerType>('customer');
	const [othersCustomerType, setOthersCustomerType] = useState<OthersCustomerType>('personal');
	const [useCustomerAddr, setUseCustomerAddr] = useState(true);
	const [useCustomerAddrBaru, setUseCustomerAddrBaru] = useState(true);

	// Sync condition toggles from API metadata
	useEffect(() => {
		const meta = assetData.assetMeta;
		if (!meta) return;
		if (meta.assetCondition === 'Used' || meta.assetType === 'SPLIT PO' || meta.assetType === 'HE') {
			setAssetType('split-po');
		} else {
			setAssetType('new');
		}
		if (meta.registrationOnBehalfOf === 'Others') {
			setBpkbOwner('others');
			if (meta.newOwnerCustomerType === 'Company') {
				setOthersCustomerType('company');
			} else {
				setOthersCustomerType('personal');
			}
		} else {
			setBpkbOwner('customer');
		}
	}, [assetData.assetMeta]);

	// Sync from user dropdown selections — when user picks "Others" or "Company" in the
	// assetBpkbBaruAtasNama / assetBpkbBaruCustomerType selects, switch the form variant.
	// Lookup label by option value because the form stores the option id (string number).
	const assetBpkbBaruAtasNamaValue = Form.useWatch('assetBpkbBaruAtasNama', form) as string | undefined;
	const assetBpkbBaruCustomerTypeValue = Form.useWatch('assetBpkbBaruCustomerType', form) as string | undefined;

	useEffect(() => {
		if (!assetBpkbBaruAtasNamaValue) return;
		const options = fieldMeta['assetBpkbBaruAtasNama']?.options ?? [];
		const selected = options.find((o) => o.value === assetBpkbBaruAtasNamaValue);
		if (!selected) return;
		const label = selected.label.toLowerCase();
		setBpkbOwner(label.includes('other') ? 'others' : 'customer');
	}, [assetBpkbBaruAtasNamaValue, fieldMeta]);

	useEffect(() => {
		if (!assetBpkbBaruCustomerTypeValue) return;
		const options = fieldMeta['assetBpkbBaruCustomerType']?.options ?? [];
		const selected = options.find((o) => o.value === assetBpkbBaruCustomerTypeValue);
		if (!selected) return;
		const label = selected.label.toLowerCase();
		setOthersCustomerType(
			label.includes('company') || label.includes('badan') ? 'company' : 'personal',
		);
	}, [assetBpkbBaruCustomerTypeValue, fieldMeta]);

	// Prefill BPKB Baru address from customer address ONLY when API returned null/empty values
	// for the newBpkbOwner address block. If BE has values, keep them as-is. Runs once when
	// the customer address from context becomes available.
	const didPrefillBpkbBaruRef = useRef(false);
	const customerAlamat = assetData.customerAddress?.alamat;
	useEffect(() => {
		if (didPrefillBpkbBaruRef.current) return;
		if (!customerAlamat) return;
		const apiHasAddress = Boolean(
			form.getFieldValue('assetBpkbBaruAlamat') ||
			form.getFieldValue('assetBpkbBaruRt') ||
			form.getFieldValue('assetBpkbBaruRw') ||
			form.getFieldValue('assetBpkbBaruKodePos') ||
			form.getFieldValue('assetBpkbBaruKelurahan') ||
			form.getFieldValue('assetBpkbBaruKecamatan') ||
			form.getFieldValue('assetBpkbBaruKota'),
		);
		if (!apiHasAddress) {
			fillAddress(form, 'assetBpkbBaru', assetData.customerAddress);
		}
		didPrefillBpkbBaruRef.current = true;
	}, [customerAlamat, assetData.customerAddress, form]);

	// Compute which of the 6 conditions is active
	const conditionNumber = (() => {
		if (assetType === 'new') {
			if (bpkbOwner === 'customer') return 1;
			return othersCustomerType === 'personal' ? 2 : 3;
		}
		if (bpkbOwner === 'customer') return 4;
		return othersCustomerType === 'personal' ? 5 : 6;
	})();

	// Visibility rules derived from the 6 mockups
	const showNamaPemilikBpkb = conditionNumber !== 6;
	const showOriginalBpkbAddress = [1, 4, 5].includes(conditionNumber);

	// ─── Build Collapse Items ────────────────────────────────────────────────

	const items: { key: string; label: string; children: React.ReactNode }[] = [];

	// ── Data Aset (varies by assetType) ──────────────────────────────────────

	if (assetType === 'new') {
		items.push({
			key: 'asset-type-header',
			label: 'New (Pass, Comm, Mesin, Chasis Only)',
			children: renderGroup([
				{ name: 'assetDealer', label: 'Dealer', type: 'text', disabled: true, required: true },
			], readOnly, fieldMeta),
		});

		items.push({
			key: 'asset-data-aset',
			label: 'Data Aset',
			children: renderGroup([
				{ name: 'assetDescription', label: 'Aset Description', type: 'text', required: true, disabled: true },
				{ name: 'assetTahunPerakitan', label: 'Tahun Perakitan', type: 'text', required: true, disabled: true },
				{ name: 'assetPhmjb', label: 'PHMJB (Perkiraan Harga Jual Mobil Bekas)', type: 'currency', required: false },
				{ name: 'assetPhmjbSpacer', label: '', type: 'spacer' },
				{ name: 'assetHargaOtr', label: 'Harga Kendaraan (OTR)', type: 'currency', required: false },
				{ name: 'assetUangMuka', label: 'Uang Muka', type: 'currency', required: false, disabled: true },
				{ name: 'assetKondisi', label: 'Kondisi Kendaraan Baru/Bekas', type: 'text', disabled: true, required: false },
				{ name: 'assetUsage', label: 'Usage', type: 'text', disabled: true, required: false },
				{ name: 'assetKategori', label: 'Kategori', type: 'select', options: [], required: false },
			], readOnly, fieldMeta),
		});
	} else {
		items.push({
			key: 'asset-type-header',
			label: 'SPLIT PO, HE, USED (COM, HE)',
			children: renderGroup([
				{ name: 'assetDescription', label: 'Asset Description', type: 'text', required: true },
				{ name: 'assetDescriptionSpacer', label: '', type: 'spacer' },
				{ name: 'assetOtrPrice', label: 'OTR Price (Asset OTR Price + Total Karoseri)', type: 'currency', required: true },
				{ name: 'assetDownPayment', label: 'Down Payment (Asset Down Payment + Total Karoseri Price)', type: 'currency', required: false },
				{ name: 'assetUsedNew', label: 'Used / New', type: 'text', disabled: true },
				{ name: 'assetUsage', label: 'Usage', type: 'text', disabled: true },
				{ name: 'assetManufacturingYear', label: 'Manufacturing Year', type: 'text' },
				{ name: 'assetManufacturingYearSpacer', label: '', type: 'spacer' },
				{ name: 'assetKategori', label: 'Kategori', type: 'select', options: [] },
				{ name: 'assetKategoriSpacer', label: '', type: 'spacer' },
				{ name: 'assetPengguna', label: 'Pengguna', type: 'text' },
				{ name: 'assetPenggunaan', label: 'Penggunaan', type: 'text' },
				{ name: 'assetLokasiPenggunaan', label: 'Lokasi Penggunaan', type: 'text' },
				{ name: 'assetLokasiPenyimpanan', label: 'Lokasi Penyimpanan', type: 'text' },
			], readOnly, fieldMeta),
		});

		items.push({
			key: 'asset-karoseri',
			label: 'Karoseri Selection',
			children: renderGroup([
				{ name: 'assetSupplierKaroseri', label: 'Supplier Karoseri', type: 'text', required: false },
				{ name: 'assetKaroseriItem', label: 'Karoseri Item', type: 'text', required: false },
				{ name: 'assetItemPricePerUnit', label: 'Item Price Per Unit', type: 'currency', required: true },
				{ name: 'assetItemQty', label: 'Item QTY', type: 'number', required: true },
				{ name: 'assetTotalDownPayment', label: 'Total Down Payment', type: 'currency', required: true },
				{ name: 'assetTotalPriceKaroseriItem', label: 'Total Price Karoseri Item', type: 'currency', required: true },
				{ name: 'assetTotalDpAllKaroseri', label: 'Total Downpayment All Karoseri Item', type: 'currency', required: false },
				{ name: 'assetTotalDpAllKaroseriSpacer', label: '', type: 'spacer' },
			], readOnly, fieldMeta),
		});
	}

	// ── Registrasi Aset ──────────────────────────────────────────────────────

	const registrasiContent = (
		<>
			{/* Original BPKB Owner */}
			{showNamaPemilikBpkb && (
				<>
					{renderGroup([
						{ name: 'assetNamaPemilikBpkb', label: 'Nama Pemilik BPKB', type: 'text', disabled: true, required: true },
					], readOnly, fieldMeta)}

					{showOriginalBpkbAddress && (
						<>
							<Divider />
							<Checkbox
								checked={useCustomerAddr}
								onChange={(e) => {
									const checked = e.target.checked;
									setUseCustomerAddr(checked);
									if (checked) {
										fillAddress(form, 'assetBpkb', assetData.customerAddress);
									}
								}}
								disabled={readOnly}
								style={{ marginBottom: 16, marginLeft: '16.667%' }}
							>
								Gunakan alamat sesuai{' '}
								<Typography.Text strong style={{ color: '#1557BC' }}>Alamat Customer</Typography.Text>
							</Checkbox>
							<QCAssetAddressSection prefix='assetBpkb' readOnly={readOnly} forceDisabled={useCustomerAddr} />
						</>
					)}

					<Divider />
				</>
			)}

			{/* BPKB Baru — Customer */}
			{bpkbOwner === 'customer' && (
				<>
					{renderGroup([
						{ name: 'assetBpkbBaruAtasNama', label: 'Pemilik BPKB Baru Atas Nama', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruNoIdentitas', label: 'No.Identitas', type: 'text', disabled: true, required: true },
						{ name: 'assetBpkbBaruNama', label: 'Nama Pemilik BPKB Baru', type: 'text', required: true },
						{ name: 'assetBpkbBaruNamaSpacer', label: '', type: 'spacer' },
					], readOnly, fieldMeta)}

					<Divider />
					<Checkbox
						checked={useCustomerAddrBaru}
						onChange={(e) => {
							const checked = e.target.checked;
							setUseCustomerAddrBaru(checked);
							if (checked) {
								fillAddress(form, 'assetBpkbBaru', assetData.customerAddress);
							}
						}}
						disabled={readOnly}
						style={{ marginBottom: 16, marginLeft: '16.667%' }}
					>
						Gunakan alamat sesuai{' '}
						<Typography.Text strong style={{ color: '#1557BC' }}>Alamat Customer</Typography.Text>
					</Checkbox>
					<QCAssetAddressSection prefix='assetBpkbBaru' readOnly={readOnly} forceDisabled={useCustomerAddrBaru} />
				</>
			)}

			{/* BPKB Baru — Others + Personal */}
			{bpkbOwner === 'others' && othersCustomerType === 'personal' && (
				<>
					{renderGroup([
						{ name: 'assetBpkbBaruAtasNama', label: 'Pemilik BPKB Baru Atas Nama', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruCustomerType', label: 'Customer Type', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruNama', label: 'Nama Pemilik BPKB', type: 'text', required: true },
						{ name: 'assetBpkbBaruNoIdentitas', label: 'No.Identitas', type: 'text', required: true },
					], readOnly, fieldMeta)}

					<Divider />

					<QCAssetAddressSection prefix='assetBpkbBaru' readOnly={readOnly} forceDisabled={false} />
				</>
			)}

			{/* BPKB Baru — Others + Company */}
			{bpkbOwner === 'others' && othersCustomerType === 'company' && (
				<>
					{renderGroup([
						{ name: 'assetBpkbBaruAtasNama', label: 'Pemilik BPKB Baru Atas Nama', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruCustomerType', label: 'Customer Type', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruJenisBadanUsaha', label: 'Jenis Badan Usaha Pemilik BPKB Baru', type: 'select', required: true, options: [] },
						{ name: 'assetBpkbBaruNoSiupp', label: 'No.SIUPP', type: 'text', required: true },
						{ name: 'assetBpkbBaruNoTdp', label: 'No.TDP', type: 'text', required: true },
						{ name: 'assetBpkbBaruNoAkta', label: 'No.Akta', type: 'text', required: true },
						{ name: 'assetBpkbBaruNoNpwp', label: 'No.NPWP', type: 'text', required: true },
						{ name: 'assetBpkbBaruNoNpwpSpacer', label: '', type: 'spacer' },
					], readOnly, fieldMeta)}

					<Divider />

					<QCAssetAddressSection prefix='assetBpkbBaru' readOnly={readOnly} forceDisabled={false} />
				</>
			)}
		</>
	);

	items.push({
		key: 'asset-registrasi',
		label: 'Registrasi Aset',
		children: registrasiContent,
	});

	// ── Asset Insurance ──────────────────────────────────────────────────────

	items.push({
		key: 'asset-insurance',
		label: 'Asset Insurance',
		children: renderGroup([
			{ name: 'assetDiasuransikanOleh', label: 'Diasuransikan oleh', type: 'select', options: [], required: false },
			{ name: 'assetAsuransiDibayarOleh', label: 'Asuransi dibayar oleh', type: 'select', options: [], required: false },
		], readOnly, fieldMeta),
	});

	// ── Employee Sections (vary by assetType) ────────────────────────────────

	if (assetType === 'new') {
		items.push({
			key: 'asset-karyawan-showroom',
			label: 'Karyawan Showroom',
			children: renderGroup([
				{ name: 'assetSalesman', label: 'Salesman', type: 'text', required: true },
				{ name: 'assetSupplierAdmin', label: 'Supplier Admin', type: 'text', required: true },
				{ name: 'assetSalesSupervisor', label: 'Sales Supervisor', type: 'text', required: true },
				{ name: 'assetSalesCoordinator', label: 'Sales Coordinator', type: 'text', required: true },
			], readOnly, fieldMeta),
		});
	} else {
		items.push({
			key: 'asset-tes-employee',
			label: 'Tes Employee',
			children: renderGroup([
				{ name: 'assetAccountOfficer', label: 'Account Officer', type: 'select', required: true, options: [] },
				{ name: 'assetSurveyor', label: 'Surveyor', type: 'select', required: false, options: [] },
				{ name: 'assetCreditAnalyst', label: 'Credit Analyst', type: 'select', required: true, options: [] },
				{ name: 'assetCreditAnalystSpacer', label: '', type: 'spacer' },
			], readOnly, fieldMeta),
		});

		items.push({
			key: 'asset-supplier-employee',
			label: 'Supplier Employee',
			children: renderGroup([
				{ name: 'assetSalesmanSp', label: 'Salesman', type: 'select', required: true, options: [] },
				{ name: 'assetSupplierAdminSp', label: 'Supplier Admin', type: 'select', required: true, options: [] },
				{ name: 'assetSalesSupervisorSp', label: 'Sales Supervisor', type: 'select', required: true, options: [] },
				{ name: 'assetSalesCoordinatorSp', label: 'Sales Coordinator', type: 'select', required: true, options: [] },
			], readOnly, fieldMeta),
		});
	}

	// ─── Render ──────────────────────────────────────────────────────────────

	return (
		<>
			{__IS_DEVELOPMENT__ && (
				<div style={{
					marginBottom: 16,
					padding: 16,
					background: '#f0f5ff',
					borderRadius: 8,
					border: '1px dashed #1557BC',
				}}>
					<Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
						[DEV] Override Kondisi Asset Data
					</Typography.Text>
					<Space direction="vertical" size={8} style={{ width: '100%' }}>
						<Space wrap>
							<Typography.Text type="secondary">Tipe Asset:</Typography.Text>
							<Radio.Group
								value={assetType}
								onChange={(e) => setAssetType(e.target.value)}
								optionType="button"
								buttonStyle="solid"
								size="small"
							>
								<Radio.Button value="new">NEW (Pass, Comm, Mesin, Chasis)</Radio.Button>
								<Radio.Button value="split-po">SPLIT PO / HE / USED (COM, HE)</Radio.Button>
							</Radio.Group>
						</Space>
						<Space wrap>
							<Typography.Text type="secondary">Pemilik BPKB Baru:</Typography.Text>
							<Radio.Group
								value={bpkbOwner}
								onChange={(e) => setBpkbOwner(e.target.value)}
								optionType="button"
								buttonStyle="solid"
								size="small"
							>
								<Radio.Button value="customer">Customer</Radio.Button>
								<Radio.Button value="others">Others</Radio.Button>
							</Radio.Group>
						</Space>
						{bpkbOwner === 'others' && (
							<Space wrap>
								<Typography.Text type="secondary">Customer Type:</Typography.Text>
								<Radio.Group
									value={othersCustomerType}
									onChange={(e) => setOthersCustomerType(e.target.value)}
									optionType="button"
									buttonStyle="solid"
									size="small"
								>
									<Radio.Button value="personal">Personal</Radio.Button>
									<Radio.Button value="company">Company</Radio.Button>
								</Radio.Group>
							</Space>
						)}
						<Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>
							Kondisi {conditionNumber}: {CONDITION_LABELS[conditionNumber]}
						</Tag>
					</Space>
				</div>
			)}

			<Collapse
				items={items}
				defaultActiveKey={items.map((i) => i.key)}
			/>
		</>
	);
};

export default QCAssetDataForm;
